import { Component, PureComponent } from "react";
import { Options, regReactPlugin } from "../react-plugin";
import { Fiber } from "../react-plugin/reactTools";
import { AnyObj } from "./types";

// 收集到的组件使用情况
export const componentCollections = new Map<string, Map<any, Set<string>>>();

let enableCollect: boolean = false;

// 更新开关
function updateCollectEnable(enableCollectSwitch: boolean) {
  enableCollect = enableCollectSwitch;
}

// 判断组件收集钩子是否开启
function isCollectEnable (): boolean {
  return enableCollect;
}

const storePrtt = /^[A-Z]\w+Store$/;
// 判断是否是Store对象
export function isStore (store: any, storeName: string) {
  if (!(typeof store === 'object')) return false;
  if (storeName && storePrtt.test(storeName)) return true;
  return false;
}
const compPatt = /^[A-Z]\w+/;
// 判断是否是 React 组件
export function isReactComponent (component: any, compName: string) {
  if (!(typeof component === 'object' || typeof component === 'function')) return false;
  if (!(compName && compPatt.test(compName))) return; // 组件名称不规范
  if (isStore(component, compName)) return; // 过滤 store
  const compTypeof = component.$$typeof?.toString();
  if (
    component.displayName || // 有 displayName 属性的组件
    component.__proto__ === Component || // Component 组件
    component.__proto__ === PureComponent || // PureComponent 组件
    compTypeof === 'Symbol(react.forward_ref)' ||  // forwardRef 组件
    compTypeof === 'Symbol(react.lazy)' || // lazy 组件
    compTypeof === 'Symbol(react.memo)' // memo 组件
  ) {
    return true;
  }
  return false;
}
// 从模块对象上抽取出所有的组件
export function getComponents (module: any, compNames?: string[], parentComp?: string) {
  if (!module) return {};
  if (!compNames) {
    compNames = Object.keys(module);
  }
  return compNames.reduce((comps, compName) => {
    const Com = module[compName];
    if (Com && isReactComponent(Com, compName)) {
      let subComs = {};
      const subComNames = Object.keys(Com);
      if (subComNames.length) {
        subComs = getComponents(Com, subComNames, compName);
      }
      comps[parentComp ? `${parentComp}.${compName}` : compName] = Com;
      Object.assign(comps, subComs); // 收集子组件
    }
    return comps;
  }, {} as any);
}

function getCurrPath () {
  return window.location.pathname;
}

function getCollectionsByPath (path?: string) {
  path = path || getCurrPath();
  let coll = componentCollections.get(path);
  if (!coll) {
    coll = new Map<any, Set<string>>();
    componentCollections.set(path, coll);
  }
  return coll;
}

const loadedModules: AnyObj = {};
const moduleComponents = new Map<any, { app: string; compName: string }>();

// 获取收集到的组件使用情况
function getCollectedComponent(path?: string) {
  // 1. 获取当前系统已经加载的模块
  const modules = Object.keys(window).filter(k => /^weapp/.test(k) && window[k]?.__esModule);
  // 2. 过滤掉已经记录过的模块，将新加载的模块的组件都记录下来
  modules.forEach(m => {
    if (loadedModules[m]) return;
    const components = getComponents(window[m]);
    Object.keys(components).forEach((compName: string) => {
      const component = components[compName];
      moduleComponents.set(component, {
        app: m,
        compName,
      });
    });
    loadedModules[m] = true;
  });
  let result: AnyObj = {};
  // 对比 componentCollections 和 moduleComponents 中的组件，找到组件对应的模块名称和组件名称
  const collections = getCollectionsByPath(path);
  const collectedComponents = collections.keys();
  let component = collectedComponents.next();
  while(!component.done) {
    const compInfo = moduleComponents.get(component.value);
    if (compInfo) {
      result[compInfo.app] = result[compInfo.app] || {} as AnyObj;
      result[compInfo.app][compInfo.compName] = collections.get(component.value);
    }
    component = collectedComponents.next();
  }
  return result;
}

// 记录组件使用情况
function collectComponent(fiber: Fiber) {
  if (!(fiber && fiber.type && fiber.pendingProps?.weId)) return;
  const mayBeCom = fiber.type;
  const isComponent = (
    (mayBeCom.displayName && !['Observer'].includes(mayBeCom.displayName)) || // 有 displayName 属性的组件
    mayBeCom.__proto__ === Component || // Component 组件
    mayBeCom.__proto__ === PureComponent || // PureComponent 组件
    ['Symbol(react.forward_ref)', 'Symbol(react.lazy)', 'Symbol(react.memo)'].includes(mayBeCom.$$typeof?.toString?.()) // forwardRef, lazy, memo
  );
  if (!isComponent) return;
  const collections = getCollectionsByPath();
  let weIds = collections.get(fiber.type);
  if (!weIds) {
    weIds = new Set<string>();
    collections.set(fiber.type, weIds);
  }
  weIds.add(fiber.pendingProps?.weId);
}

// 通过 react 底层钩子，收集 Class 组件使用情况
regReactPlugin('classComponentDidMount', (options: Options) => {
  try {
    if (isCollectEnable()) {
      const fiber = options?.fiber;
      collectComponent(fiber);
    }
  } catch (e) {}
  return options;
});

// 通过 react 底层钩子，收集 Function/ForwardRef/MemoComponent 组件使用情况
regReactPlugin('functionComponentDidMount', (options: Options) => {
  try {
    if (isCollectEnable()) {
      const fiber = options?.fiber;
      collectComponent(fiber);
    }
  } catch (e) {}
  return options;
});

export {
  isCollectEnable,
  updateCollectEnable,
  getCollectedComponent,
}